/**
 * The app-level drain: empties every match's queue whenever the app is open, whatever screen
 * it is on.
 *
 * This is the path that must always work. iOS has no Background Sync at all, and on Android
 * the service worker can be killed at any moment, so nothing may depend on the worker being
 * alive. Everything the worker does, this does too, from the page.
 *
 * ## What triggers a drain, and why each one is here
 *
 * - **Starting.** A scorer who finished offline and reopens the app has balls waiting; they
 *   should not have to find the match to send them.
 * - **`visibilitychange` to visible, `pageshow`, `online`.** Each is a moment the network may
 *   have changed. These call `onNetworkOnline`, which also forgets the backoff and lifts a 401
 *   block — the backoff clock is stale after a suspension, and signing in is what unblocks.
 *   `pageshow` is separate because a page restored from the back-forward cache fires neither
 *   of the others on iOS.
 * - **A 30-second timer while visible.** `navigator.onLine` is true on a captive portal and on
 *   wifi with no route out, so the events alone can leave the queue stuck. The timer is the
 *   fallback for a network that lies. Skipped while hidden (phones throttle it anyway) and
 *   while the previous tick is still running, so a drain in backoff is not stacked behind.
 * - **After every ball**, from the ledger — not here, because it is per write.
 *
 * There is deliberately no check of `navigator.onLine` before trying (`seemsOnline` in the
 * transport says why): a wrong "offline" would strand balls on exactly the networks this
 * exists for.
 */
import type { Drainer } from "@sportsos/sync";

/** The slice of the platform this needs. An interface so tests need no browser. */
export interface ForegroundEnv {
  readonly window: EventTarget;
  readonly document: EventTarget & { readonly visibilityState: string };
  readonly setInterval: (callback: () => void, ms: number) => unknown;
  readonly clearInterval: (id: unknown) => void;
}

export const FOREGROUND_INTERVAL_MS = 30_000;

const browserEnv = (): ForegroundEnv => ({
  window,
  document,
  setInterval: (callback, ms) => window.setInterval(callback, ms),
  clearInterval: (id) => window.clearInterval(id as number),
});

/** Start draining. Returns the way to stop, for tests and hot reload. */
export function startForegroundDrain(
  drainer: Pick<Drainer, "drainAll" | "onNetworkOnline">,
  env: ForegroundEnv = browserEnv(),
  /**
   * Called when the app goes to the background. The moment to ask the browser for a
   * background drain, since a page about to be suspended may never get another chance.
   */
  onBackground: () => void = (): void => undefined,
): () => void {
  /** A tick still running — usually a drain waiting out a backoff. */
  let inFlight = false;

  const swallow = (): undefined => undefined;

  const tick = (): void => {
    if (env.document.visibilityState !== "visible" || inFlight) return;
    inFlight = true;
    void drainer
      .drainAll()
      .catch(swallow)
      .finally(() => {
        inFlight = false;
      });
  };

  const wake = (): void => {
    void drainer.onNetworkOnline().catch(swallow);
  };
  const onVisibility = (): void => {
    if (env.document.visibilityState === "visible") wake();
    else onBackground();
  };

  // Not `tick()`: the boot drain must run even if the page was opened in the background.
  inFlight = true;
  void drainer
    .drainAll()
    .catch(swallow)
    .finally(() => {
      inFlight = false;
    });

  env.window.addEventListener("online", wake);
  env.window.addEventListener("pageshow", wake);
  env.document.addEventListener("visibilitychange", onVisibility);
  const timer = env.setInterval(tick, FOREGROUND_INTERVAL_MS);

  return () => {
    env.window.removeEventListener("online", wake);
    env.window.removeEventListener("pageshow", wake);
    env.document.removeEventListener("visibilitychange", onVisibility);
    env.clearInterval(timer);
  };
}
